import { ELEMENT_CLASS, ELEMENT_ID } from "./constants.js";
import { computed, onScopeDispose, ref, unref, watch } from "vue";
import { autoUpdate, flip, shift, useFloating } from "@floating-ui/vue";
//#region src/components/ScalarTooltip/useTooltip.ts
/**
* The delay timer for the tooltip
*
* If there's not a timer running it should be undefined
*/
var timer = ref();
/**
* A reference to the tooltip element
*
* If the hook hasn't been initialized it should be undefined
*/
var el = ref();
/**
* The configuration for the active tooltip
*
* If no tooltip is active it should be undefined
*/
var config = ref();
var { floatingStyles, placement, update } = useFloating(computed(() => unref(config.value?.targetRef)), el, {
	placement: computed(() => unref(config.value?.placement)),
	whileElementsMounted: autoUpdate,
	middleware: computed(() => [flip(), shift()])
});
watch(placement, (value) => {
	if (!el.value || !value) return;
	el.value.dataset.side = value.split("-")[0];
});
watch(floatingStyles, () => {
	if (!el.value) return;
	el.value.style.position = floatingStyles.value.position;
	el.value.style.top = floatingStyles.value.top;
	el.value.style.left = floatingStyles.value.left;
	el.value.style.transform = floatingStyles.value.transform ?? "";
	el.value.style.willChange = floatingStyles.value.willChange ?? "";
});
watch(config, (opts) => {
	if (!el.value) return;
	if (opts) {
		if (moveTooltipToHost(unref(opts.targetRef))) update();
		const contentTarget = unref(opts?.contentTarget) ?? "textContent";
		el.value[contentTarget] = unref(opts?.content) ?? "";
		const offset = unref(opts?.offset) ?? 4;
		el.value.style.setProperty("--scalar-tooltip-offset", `${offset}px`);
		el.value.dataset.side = (unref(opts?.placement) ?? "bottom").split("-")[0];
		el.value.style.setProperty("display", "block");
	} else {
		el.value.innerHTML = "";
		el.value.style.removeProperty("--scalar-tooltip-offset");
		el.value.style.setProperty("display", "none");
		moveTooltipToHost(void 0);
	}
}, { deep: true });
/**
* Initialize the tooltip element
*
* If the tooltip is already initialized it will be ignored
*/
function initializeTooltipElement() {
	if (typeof document === "undefined" || typeof window === "undefined") return;
	if (el.value) return;
	const existingTooltipElement = document.getElementById(ELEMENT_ID);
	if (existingTooltipElement) el.value = existingTooltipElement;
	else {
		el.value = document.createElement("div");
		el.value.role = "tooltip";
		el.value.id = ELEMENT_ID;
		el.value.classList.add(ELEMENT_CLASS);
		el.value.classList.add("scalar-app");
		el.value.style.setProperty("display", "none");
		el.value.addEventListener("mouseleave", hideTooltip);
		document.body.appendChild(el.value);
	}
}
/**
* Hide the tooltip
*
* If the mouse is moving between the tooltip and the target we don't hide the tooltip
*/
function hideTooltip(_e) {
	if (!isMovingOffElements(_e)) return;
	clearTimer();
	config.value = void 0;
}
/**
* Handle the escape key
*
* If the escape key is pressed we need to hide the tooltip
*
* @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tooltip_role#keyboard_interactions
*/
function handleEscape(e) {
	if (e.key === "Escape") {
		e.stopPropagation();
		hideTooltip(e);
	}
}
/**
* Find the modal dialog containing an element, if there is one
*
* Only a dialog opened with `showModal()` is promoted to the browser's top layer, and
* `:modal` matches exactly those. The `open` property is not a substitute: it is also
* true for `dialog.show()`, which leaves the dialog in the normal flow where the
* tooltip already paints correctly.
*/
function getModalDialog(target) {
	const dialog = target?.closest("dialog");
	if (!dialog) return;
	try {
		return dialog.matches(":modal") ? dialog : void 0;
	} catch {
		return;
	}
}
/**
* Move the tooltip element into the host it needs to paint above
*
* Top layer content paints above the rest of the document whatever `z-index` anything
* else carries, so the only way to sit above a modal dialog is to live inside it.
* Everywhere else the tooltip belongs on the body.
*
* This doubles as a repair step: appending an element that is no longer in the document
* puts it back, which covers a dialog being unmounted while the tooltip sat inside it.
*
* Returns whether the element actually moved.
*/
function moveTooltipToHost(target) {
	if (!el.value) return false;
	const host = getModalDialog(target) ?? document.body;
	if (el.value.parentElement === host) return false;
	host.appendChild(el.value);
	return true;
}
/** Clears the current timer */
function clearTimer() {
	if (timer.value) {
		clearTimeout(timer.value);
		timer.value = void 0;
	}
}
/** Get all the parents of an element */
function getAllParents(el) {
	const parents = [];
	let parent = el.parentElement;
	while (parent) {
		parents.push(parent);
		parent = parent.parentElement;
	}
	return parents;
}
/** Check if mouse moved off the target but onto the tooltip */
function isMovingOffElements(e) {
	const target = unref(config.value?.targetRef);
	if (e instanceof MouseEvent && e.relatedTarget instanceof Element && target) {
		const relatedTargetParents = getAllParents(e.relatedTarget);
		return e.relatedTarget.id !== "scalar-tooltip" && !relatedTargetParents.some((parent) => parent.id === "scalar-tooltip") && e.relatedTarget !== target;
	}
	return true;
}
/**
* Create a tooltip
*
* If there isn't a tooltip element it will be created
*/
function useTooltip(opts) {
	initializeTooltipElement();
	/**
	* Show the tooltip after the delay if configured
	*/
	function showTooltipAfterDelay(_e) {
		const delay = unref(opts.delay) ?? 300;
		clearTimer();
		if (delay > 0) timer.value = setTimeout(() => showTooltip(_e), delay);
		else showTooltip(_e);
	}
	/**
	* Show the tooltip
	*/
	function showTooltip(_e) {
		clearTimer();
		document.addEventListener("keydown", handleEscape, {
			once: true,
			capture: true
		});
		moveTooltipToHost(unref(opts.targetRef));
		config.value = opts;
	}
	watch(() => unref(opts.targetRef), (newRef, oldRef) => {
		if (oldRef) {
			oldRef.removeEventListener("mouseenter", showTooltipAfterDelay);
			oldRef.removeEventListener("mouseleave", hideTooltip);
			oldRef.removeEventListener("focus", showTooltip);
			oldRef.removeEventListener("blur", hideTooltip);
			oldRef.removeAttribute("aria-describedby");
		}
		if (newRef) {
			newRef.addEventListener("mouseenter", showTooltipAfterDelay);
			newRef.addEventListener("mouseleave", hideTooltip);
			newRef.addEventListener("focus", showTooltip);
			newRef.addEventListener("blur", hideTooltip);
			newRef.setAttribute("aria-describedby", ELEMENT_ID);
		}
	}, { immediate: true });
	onScopeDispose(() => {
		clearTimer();
		const target = unref(opts.targetRef);
		if (target) {
			target.removeEventListener("mouseenter", showTooltipAfterDelay);
			target.removeEventListener("mouseleave", hideTooltip);
			target.removeEventListener("focus", showTooltip);
			target.removeEventListener("blur", hideTooltip);
			target.removeAttribute("aria-describedby");
		}
		if (unref(config.value?.targetRef) === unref(opts.targetRef)) config.value = void 0;
	});
}
//#endregion
export { useTooltip };

//# sourceMappingURL=useTooltip.js.map